// COP — "COP Copilot" dockable panel (React shell only; never enters the render loop).
//
// A dockable, themed analyst panel that renders the C3 copilot capability's conversation.
// It is PURE React over the capability's CopilotApi (ask / results / subscribe) — it holds no
// envelope/crypto/PDP logic and never touches HostContext. The copilot reasons ONLY over the
// host-filtered (PDP-visible) working set, so a denied object never reaches this panel.
//
// Wave A: the copilot returns a canned analyst turn (no model runtime — that is Wave B). This
// panel is the UI mounted by App.tsx (<CopilotPanel api={api} />), following the existing panel
// layout (mirrors ChatPanel). React lives HERE, out of the capability/render loop.
//
// Themed via the design-tokens CSS variables on :root (CONTRACT:C4-DESIGN-TOKENS.1.0).

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CopilotApi, CopilotListItem, CopilotTurn } from "../capabilities/copilotApi.ts";
import "./copilot.css";

/** Short HH:MM clock for a turn timestamp. */
function clock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--:--";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** APP-6 affiliation → the shell's --status-* token (Rule 2: hue = affiliation only). The dot on a
 *  ranked-list row is coloured by the REAL affiliation the item carries; absent → the unknown hue.
 *  No raw hex here — the token resolves to APP6_AFFILIATION_COLORS via INTEL_SKIN_THEME. */
function affDotColor(aff: CopilotListItem["affiliation"]): string {
  switch (aff) {
    case "hostile":
      return "var(--status-hostile, #FF5256)";
    case "friendly":
      return "var(--status-friendly, #4DA6FF)";
    case "neutral":
      return "var(--status-neutral, #46D98E)";
    case "pending":
      return "var(--status-pending, #FF9E40)";
    case "unknown":
    default:
      return "var(--status-unknown, #F2C14E)";
  }
}

/** Compact classification-band → token color (the small session banner). NOT the full DoD
 *  banner builder (that is dod-marking.ts, owned by another lane) — a calm in-panel marking
 *  that names the high-water classification of the session's working data. Falls back to the
 *  neutral surface for an unknown/absent band. Display-only; the PDP gates the real working set. */
function bandColor(band: string): { bg: string; fg: string } {
  switch (band.toUpperCase().replace(/[\s-]+/g, "_")) {
    case "TOP_SECRET":
      return { bg: "var(--classbar-ts, #FF8C00)", fg: "#1a1205" };
    case "SECRET":
      return { bg: "var(--classbar-secret, #C8102E)", fg: "#fff" };
    case "CONFIDENTIAL":
      return { bg: "var(--classbar-confidential, #0033A0)", fg: "#fff" };
    case "CUI":
      return { bg: "var(--classbar-cui, #502B85)", fg: "#fff" };
    case "UNCLASSIFIED":
      return { bg: "var(--classbar-unclass, #007A33)", fg: "#fff" };
    default:
      return { bg: "var(--ui-surface-raised, #2a323c)", fg: "var(--ui-text, #c7d0db)" };
  }
}

export function CopilotPanel({
  api,
  onEject,
  onSelect,
  sessionClassification,
}: {
  api: CopilotApi;
  onEject?: () => void;
  /** C1 — CITE→MAP seam: select + fly-to the cited entity (wired in App.tsx to handle.select).
   *  Absent → the CITE chip stays an inert label (no dead button). */
  onSelect?: (entityId: string) => void;
  /** SECURITY-state banner: the session's data classification high-water (status.highWater.
   *  classification). Wired in App.tsx off the host status. Renders a small marking at the top of
   *  the copilot so the operator always sees the level the copilot is reasoning at. ABSENT ⇒ no
   *  banner (back-compat). Display-only — the host-filtered working set is the real boundary. */
  sessionClassification?: string;
}): JSX.Element {
  const [turns, setTurns] = useState<CopilotTurn[]>(() => api.results());
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  // C4 — the header's ACTING-AS tag, re-read on every conversation emit so a persona switch
  // (api.setPersonaShort → emit) relabels the header without a new ask. Falls back to `persona`.
  const [personaShort, setPersonaShort] = useState<string>(() => api.personaShort ?? api.persona);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Subscribe to the conversation stream (off the render loop). Re-reads personaShort on every
  // emit so setPersonaShort relabels the header (C4) — setPersonaShort calls emit().
  useEffect(
    () =>
      api.subscribe((next) => {
        setTurns(next);
        setPersonaShort(api.personaShort ?? api.persona);
      }),
    [api],
  );

  // Keep the list pinned to the newest turn.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  const submit = (): void => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    void api
      .ask(text)
      .finally(() => setBusy(false));
    setDraft("");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // EJECT the agent: clear the thread (api.eject) AND close the dock (host onEject). An ejected
  // agent leaves NO conversation behind — distinct from the old "✕ CLOSE" which merely hid the panel.
  const eject = (): void => {
    api.eject();
    onEject?.();
  };

  return (
    <section className="panel copilot" aria-label="COP Copilot">
      <header className="copilot__header">
        <div className="copilot__id">
          <span className="copilot__dot" aria-hidden="true" />
          <span className="copilot__title">COP COPILOT</span>
          <span className="copilot__sub">
            AI-AGENT · ACTING AS {personaShort} · SCOPED TO YOUR CLEARANCE
          </span>
          {/* EPHEMERALITY badge — the guarantee made visible: the host purges this conversation AND
              the copilot's read-side working set on a persona switch, a query-level downgrade, and
              logout, so no higher-classification Q&A turn or row survives into a lower view. Display
              only (the host hooks are the real boundary); turns the guarantee into a talking point. */}
          <span
            className="copilot__ephemeral"
            title="Nothing is retained: this conversation and the copilot's working set are wiped on a persona switch, a clearance downgrade, and logout — no higher-classification answer or row survives into a lower view."
          >
            <span className="copilot__ephemeral-dot" aria-hidden="true" />
            EPHEMERAL · NO HISTORY RETAINED
          </span>
        </div>
        <button
          type="button"
          className="copilot__eject"
          onClick={eject}
          title="Eject agent — clears this conversation"
          aria-label="Eject agent and clear conversation"
        >
          ⏏ EJECT
        </button>
      </header>

      {/* SECURITY-state classification banner — the session's data high-water (status.highWater.
          classification). Shows the operator the level the copilot is reasoning AT. On a downgrade
          the host wipes the copilot (eject) AND drops the banner to the lower level. Display-only;
          the host-filtered working set is the real Glomar boundary. Absent prop ⇒ no banner. */}
      {sessionClassification ? (
        (() => {
          const c = bandColor(sessionClassification);
          const label = sessionClassification.toUpperCase().replace(/_/g, " ");
          return (
            <div
              className="copilot__classbar"
              role="status"
              aria-label={`Copilot session classification: ${label}`}
              style={{
                background: c.bg,
                color: c.fg,
                textAlign: "center",
                fontFamily: "var(--typography-family-mono, ui-monospace, monospace)",
                fontSize: "var(--typography-scale-xs, 11px)",
                fontWeight: 700,
                letterSpacing: "0.12em",
                padding: "2px 8px",
              }}
            >
              {label}
            </div>
          );
        })()
      ) : null}

      <div className="copilot__list" ref={listRef} role="log" aria-live="polite">
        {turns.length === 0 ? (
          <div className="copilot__empty">Ask the copilot about the visible picture</div>
        ) : (
          turns.map((t) => (
            <div key={t.id} className={`copilot__turn copilot__turn--${t.role}`}>
              <div className="copilot__turn-meta">
                <span className="copilot__role">{t.role === "user" ? "You" : "Copilot"}</span>
                <span className="copilot__time">{clock(t.time)}</span>
              </div>
              <div className="copilot__text">{t.text}</div>
              {t.marking ? (
                <div className="copilot__marking" aria-label="Classification roll-up">
                  {t.marking}
                </div>
              ) : null}
              {t.result ? (
                <div className="copilot__result" aria-label="Query result">
                  {t.result.rows_returned} row{t.result.rows_returned === 1 ? "" : "s"}
                </div>
              ) : null}
              {/* Ranked-list pattern (the mock's "top N"): rank badge + REAL-affiliation dot +
                  call/meta + a CITE chip. Rendered ONLY when the runtime returns structured rows
                  — we never fabricate ranked data. C1: the CITE chip is now LIVE when an onSelect
                  seam is wired (App.tsx → handle.select) — clicking it SELECTs + flies the map to
                  the cited entity (`it.ref`). Without the seam it degrades to an inert label (no
                  dead button). Glomar-safe: a denied object never lands in this list, and
                  handle.select resolves a hidden/unknown id to a no-op deselect. */}
              {t.list && t.list.length > 0 ? (
                <div className="copilot__list-rows" aria-label="Ranked results">
                  {t.list.map((it) => (
                    <div
                      key={`${t.id}:rank:${it.rank}:${it.ref ?? it.call}`}
                      className="copilot__list-item"
                    >
                      <span className="copilot__rank">{it.rank}</span>
                      <span
                        className="copilot__aff-dot"
                        aria-hidden="true"
                        style={{ background: affDotColor(it.affiliation) }}
                      />
                      <div className="copilot__list-body">
                        <div className="copilot__call">{it.call}</div>
                        <div className="copilot__meta">{it.meta}</div>
                      </div>
                      {it.ref ? (
                        onSelect ? (
                          // LIVE CITE chip — select + fly-to the cited entity on the map (C1).
                          <button
                            type="button"
                            className="copilot__cite copilot__cite--live"
                            aria-label={`Show ${it.call} on the map`}
                            title="Select + fly to this entity on the map"
                            onClick={() => onSelect(it.ref as string)}
                          >
                            {it.cta ?? "CITE ↗"}
                          </button>
                        ) : (
                          // Inert label fallback when no panel→map select seam is wired.
                          <span className="copilot__cite" aria-label={`Citation: ${it.ref}`}>
                            {it.cta ?? "CITE ↗"}
                          </span>
                        )
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
              {t.citations && t.citations.length > 0 ? (
                <ul className="copilot__citations" aria-label="Citations">
                  {t.citations.map((c) => (
                    <li key={`${t.id}:${c.index}:${c.ref}`} className="copilot__citation">
                      [{c.index}] {c.label ?? c.ref}
                    </li>
                  ))}
                </ul>
              ) : null}
              {t.coverage ? <div className="copilot__coverage">{t.coverage}</div> : null}
              {t.note ? <div className="copilot__note">{t.note}</div> : null}
              {/* Action pills (the mock's hasActions row) — teal pills that run client-side. */}
              {t.actions && t.actions.length > 0 ? (
                <div className="copilot__actions">
                  {t.actions.map((a, i) => (
                    <button
                      key={`${t.id}:act:${i}:${a.label}`}
                      type="button"
                      className="copilot__action-btn"
                      onClick={a.act}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>

      <div className="copilot__composer">
        <input
          className="copilot__input"
          type="text"
          value={draft}
          placeholder="Ask the copilot…"
          aria-label="Ask the copilot"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={busy}
        />
        <button
          className="copilot__send"
          type="button"
          onClick={submit}
          disabled={draft.trim().length === 0 || busy}
        >
          Ask
        </button>
      </div>
    </section>
  );
}
